import { RailNavButton } from "@aisquare/ui";
import { useMotionValue } from "motion/react";
import { Home, Compass, FolderOpen, Settings } from "lucide-react";
import { Section, VariantBlock } from "../lib/Section";

/**
 * A vertical proximity-magnify dock: a shared `mousePos` motion value (the
 * cursor's clientY) is fed to every tile, so the tile nearest the cursor
 * spring-enlarges and its neighbours ride the shoulder — the macOS-dock feel,
 * the SAME ramp MorphingRail uses. Tiles run in tooltip mode so the magnify
 * reads as a dock of glass icons rather than an inline-labelled rail.
 */
function MagnifyDock() {
  const mousePos = useMotionValue<number>(Infinity);
  const items = [
    { icon: <Home />, label: "Home", active: true },
    { icon: <Compass />, label: "Explore", active: false },
    { icon: <FolderOpen />, label: "Library", active: false },
    { icon: <Settings />, label: "Settings", active: false },
  ];
  return (
    <div
      onMouseMove={(e) => mousePos.set(e.clientY)}
      onMouseLeave={() => mousePos.set(Infinity)}
      className="flex w-fit flex-col items-center gap-2 rounded-2xl border border-border bg-card p-3"
    >
      {items.map((it) => (
        <RailNavButton
          key={it.label}
          icon={it.icon}
          label={it.label}
          labelMode="tooltip"
          variant={it.active ? "active" : "inactive"}
          mousePos={mousePos}
          axis="vertical"
          className="w-auto"
        />
      ))}
    </div>
  );
}

export function RailNavButtonDemo() {
  return (
    <Section
      id="rail-nav-button"
      title="RailNavButton"
      description="A left-rail navigation control rendered as a large glass icon tile. The name shows at rest beside the tile, and the tile smoothly spring-enlarges on hover or keyboard focus (the macOS-dock magnify feel). Pass a shared cursor motion value to drive proximity magnify across a whole rail. Reduced-motion users get a static tile."
    >
      <VariantBlock
        label="Name at rest"
        caption="default — glass tile with the label visible; hover or tab to spring-enlarge the tile"
      >
        <div className="flex w-56 flex-col gap-1 rounded-lg border border-border bg-card p-2">
          <RailNavButton icon={<Home />} label="Home" variant="active" />
          <RailNavButton icon={<Compass />} label="Explore" />
          <RailNavButton icon={<FolderOpen />} label="Library" />
          <RailNavButton icon={<Settings />} label="Settings" />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Proximity magnify dock"
        caption="mouse over the strip — the tile nearest the cursor spring-enlarges (shared mousePos), the same dock ramp as MorphingRail"
      >
        <MagnifyDock />
      </VariantBlock>

      <VariantBlock
        label="Hover-reveal label"
        caption="labelMode='hover' — the legacy collapsed-at-rest rail; the name slides in on hover / focus"
      >
        <div className="flex w-56 flex-col gap-1 rounded-lg border border-border bg-card p-2">
          <RailNavButton icon={<Home />} label="Home" variant="active" labelMode="hover" />
          <RailNavButton icon={<Compass />} label="Explore" labelMode="hover" />
          <RailNavButton icon={<FolderOpen />} label="Library" labelMode="hover" />
        </div>
      </VariantBlock>

      <VariantBlock label="Compact size" caption="size='compact' for the rail's narrow breakpoint">
        <div className="flex w-48 flex-col gap-1 rounded-lg border border-border bg-card p-2">
          <RailNavButton icon={<Home />} label="Home" variant="active" size="compact" />
          <RailNavButton icon={<Compass />} label="Explore" size="compact" />
        </div>
      </VariantBlock>
    </Section>
  );
}
